import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import reportWebVitals from './reportWebVitals';
import { BreakpointProvider } from 'react-socks';
import Router from './Router';
import { createTheme } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { AuthProvider } from './utils/AuthContext';
import { trackEvent, captureCampaignContext } from './utils/analytics';
import { MuiButtonGlobalStyles } from './muiStyles';
import { Snackbar, Alert } from '@mui/material';
import { useState, useEffect } from 'react';
import { messaging, setupCapacitorPushNotifications } from './utils/firebase';
import { notificationDataToAppRoute } from './utils/notificationRoute';
import { Capacitor } from '@capacitor/core';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { initFacebookPixel } from './utils/facebookPixel';
import { ensureIosSmartBanner } from './utils/iosSmartBanner';

const isIosAppStoreBannerEnabled = String(import.meta.env.VITE_ENABLE_IOS_APP_STORE_BANNER ?? '').toLowerCase() === 'true';
const iosAppStoreId = import.meta.env.VITE_IOS_APP_STORE_ID || '6780835251';

// Capture UTM params before any redirect (e.g. /demo -> /team/demo/home) strips the URL.
captureCampaignContext();

if (!Capacitor.isNativePlatform()) {
  const normalizedPath = window.location.pathname.replace(/\/+$/, '') || '/';
  if (normalizedPath === '/help' && !window.location.hash) {
    window.location.replace(`${window.location.origin}/#/help`);
  }
}

if (!Capacitor.isNativePlatform() && isIosAppStoreBannerEnabled && iosAppStoreId) {
  ensureIosSmartBanner(iosAppStoreId);
}

// Initialize Firebase Authentication for native platforms
if (Capacitor.isNativePlatform()) {
  FirebaseAuthentication.useAppLanguage();
  console.log('✅ Firebase Authentication initialized for native platform');

  if (Capacitor.getPlatform() === 'ios') {
    document.documentElement.classList.add('native-ios');

    // Keep iOS native viewport at 1:1 so input focus does not trigger
    // disruptive page zoom/clipping behavior.
    const viewportMeta = document.querySelector('meta[name="viewport"]');
    if (viewportMeta) {
      viewportMeta.setAttribute(
        'content',
        'width=device-width, initial-scale=1.0, viewport-fit=cover'
      );
    }
  }
}

initFacebookPixel();

// Global error handler for iOS debugging
// Captures all JavaScript errors and logs them to Xcode console
window.addEventListener('error', (event) => {
  const errorDetails = {
    message: event.message || 'Unknown error',
    filename: event.filename || 'unknown file',
    lineno: event.lineno || 'unknown line',
    colno: event.colno || 'unknown column',
    error: event.error ? {
      name: event.error.name,
      message: event.error.message,
      stack: event.error.stack
    } : 'No error object available'
  };

  // "Script error." is a cross-origin error from external scripts (usually Firebase SDK on iOS)
  // These are typically non-blocking and can be ignored
  if (errorDetails.message === 'Script error.') {
    console.warn('⚠️ Cross-origin script error detected (likely Firebase SDK internal - can be ignored)');
    console.warn('⚠️ Filename:', errorDetails.filename);
    // Don't block on these errors
    return;
  }

  console.error('🔴 Global JavaScript Error:', JSON.stringify(errorDetails, null, 2));
  console.error('Error object:', event.error);

  // Don't prevent default - we want to see the actual error
  // event.preventDefault();
});

// Unhandled promise rejection handler
window.addEventListener('unhandledrejection', (event) => {
  console.error('🔴 Unhandled Promise Rejection:', {
    reason: event.reason,
    promise: event.promise,
    message: event.reason?.message || event.reason,
    stack: event.reason?.stack || 'No stack trace'
  });
  console.error('Rejection reason:', event.reason);

  // Don't prevent default - we want to see the actual error
  // event.preventDefault();
});

// Only register service worker when NOT running as native app
// Enhanced service worker registration for PWA notifications on mobile browsers
if ('serviceWorker' in navigator && !Capacitor.isNativePlatform()) {
  navigator.serviceWorker.register('/firebase-messaging-sw.js', {
    scope: '/',
    // Update service worker on page load to ensure latest version
    updateViaCache: 'none'
  })
    .then(registration => {
      console.log('✅ Service Worker registered successfully:', registration.scope);

      // Check for updates periodically (important for PWA)
      setInterval(() => {
        registration.update().catch(err => {
          console.warn('⚠️ Service Worker update check failed:', err);
        });
      }, 60000); // Check every minute

      // Handle service worker updates
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              console.log('🔄 New service worker available, will activate on next page load');
            }
          });
        }
      });
    })
    .catch(err => {
      console.error('🔴 Service Worker registration failed:', err);
      console.error('🔴 This will prevent PWA push notifications from working');
    });
}

const theme = createTheme({
  components: {
    MuiTextField: {
      defaultProps: {
        InputLabelProps: { shrink: true },
      },
    },
  },
});

window.addEventListener('vite:preloadError', () => {
  // Hard reload is necessary for Vite preload errors
  // Avoid reload on native platforms as it can cause white screens
  if (!Capacitor.isNativePlatform()) {
    window.location.reload();
  } else {
    console.error('Vite preload error on native platform - manual restart required');
  }
})

// Error Boundary to catch React rendering errors
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null; errorInfo: React.ErrorInfo | null }
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('🔴 React Error Boundary caught an error:');
    console.error('Error:', error);
    console.error('Error name:', error.name);
    console.error('Error message:', error.message);
    console.error('Error stack:', error.stack);
    console.error('Component stack:', errorInfo.componentStack);
    console.error('Full error info:', JSON.stringify({
      errorName: error.name,
      errorMessage: error.message,
      errorStack: error.stack,
      componentStack: errorInfo.componentStack
    }, null, 2));

    this.setState({ error, errorInfo });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '20px', fontFamily: 'monospace' }}>
          <h1>⚠️ Application Error</h1>
          <p><strong>Error:</strong> {this.state.error?.message}</p>
          <details style={{ whiteSpace: 'pre-wrap' }}>
            <summary>Error Details (check Xcode console for full trace)</summary>
            <p><strong>Stack:</strong></p>
            <pre style={{ maxWidth: '100%', overflowX: 'auto' }}>{this.state.error?.stack}</pre>
            <p><strong>Component Stack:</strong></p>
            <pre style={{ maxWidth: '100%', overflowX: 'auto' }}>{this.state.errorInfo?.componentStack}</pre>
          </details>
          <button onClick={() => window.location.href = '/'}>Reload App</button>
        </div>
      );
    }
    return this.props.children;
  }
}

function FirebaseSnackbar() {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    // Handle web push notifications via Firebase messaging
    if (messaging && !Capacitor.isNativePlatform()) {
      // Dynamically import onMessage only for web platforms
      import('firebase/messaging')
        .then(({ onMessage }) => {
          const unsubscribe = onMessage(messaging, (payload) => {
            setMessage(payload?.data?.body || 'A new notification received.');
            setOpen(true);
          });
        })
        .catch(error => {
          console.error('🔴 Failed to import firebase/messaging:', error);
        });
    }

    // Handle native push notifications via Capacitor
    if (Capacitor.isNativePlatform()) {
      try {
        setupCapacitorPushNotifications(
          (notification) => {
            setMessage(notification.body || 'A new notification received.');
            setOpen(true);
          },
          (data) => {
            const route = notificationDataToAppRoute(data);
            if (route) window.location.hash = route;
          }
        );
        console.log('✅ Capacitor push notifications setup complete');
      } catch (error) {
        console.error('🔴 Failed to setup Capacitor push notifications:', error);
      }
    }
  }, []);

  return (
    <Snackbar open={open} autoHideDuration={6000} onClose={() => setOpen(false)}>
      <Alert onClose={() => setOpen(false)} severity="success" sx={{ width: '100%' }}>
        {message}
      </Alert>
    </Snackbar>
  );
}

const rootElement = document.getElementById('root');
if (!rootElement) {
  console.error('🔴 CRITICAL: Root element not found!');
  throw new Error('Root element not found in DOM');
}

const root = ReactDOM.createRoot(rootElement as HTMLElement);

try {
  root.render(
    <React.StrictMode>
      <ErrorBoundary>
        <AuthProvider>
          <ThemeProvider theme={theme}>
            {MuiButtonGlobalStyles}
            <FirebaseSnackbar />
            <BreakpointProvider>
              <Router />
            </BreakpointProvider>
          </ThemeProvider>
        </AuthProvider>
      </ErrorBoundary>
    </React.StrictMode>
  );
  console.log('✅ React render call completed');
} catch (error) {
  console.error('🔴 CRITICAL: Failed to render React app:', error);
  throw error;
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals((metric) => {
  void trackEvent('web_vitals', {
    metric_name: metric.name,
    // CLS is a unitless fraction; scale it so GA4 gets an integer like the ms-based metrics.
    metric_value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
    metric_id: metric.id,
  });
});
